import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Key,
  Link,
  RefreshCw,
  Copy,
  CheckCircle,
  XCircle,
  Loader2,
  Settings,
  History,
  Shield,
  Webhook
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMIntegrationPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [config, setConfig] = useState({
    crm_api_key: '',
    crm_webhook_url: '',
    webhook_enabled: false
  });
  const [newApiKey, setNewApiKey] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchConfig();
    fetchRequests();
  }, [token]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/crm-config`, axiosConfig);
      setConfig(response.data);
      setWebhookUrl(response.data.crm_webhook_url || '');
    } catch (error) {
      toast.error('Failed to load CRM configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/external-requests?limit=20`, axiosConfig);
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  const generateNewKey = async () => {
    setGeneratingKey(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/crm-config/generate-key`, {}, axiosConfig);
      setNewApiKey(response.data.api_key);
      toast.success('New API key generated! Copy it now - it will not be shown again.');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to generate API key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const saveWebhookConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/crm-config`, {
        crm_webhook_url: webhookUrl,
        webhook_enabled: config.webhook_enabled
      }, axiosConfig);
      toast.success('Webhook configuration saved');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleWebhook = async (enabled) => {
    try {
      await axios.post(`${API_URL}/api/admin/crm-config`, {
        webhook_enabled: enabled
      }, axiosConfig);
      setConfig(prev => ({ ...prev, webhook_enabled: enabled }));
      toast.success(enabled ? 'Webhook enabled' : 'Webhook disabled');
    } catch (error) {
      toast.error('Failed to update webhook status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="crm-integration-page">
        <div>
          <h1 className="text-2xl font-bold font-heading">External Integrations</h1>
          <p className="text-muted-foreground">Configure CRM API access and webhooks</p>
        </div>

        {/* API Key Section */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              CRM API Key
            </CardTitle>
            <CardDescription>
              Use this API key to authenticate external CRM requests
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>Current API Key</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input 
                    value={config.crm_api_key || 'No API key configured'} 
                    readOnly 
                    className="font-mono bg-muted"
                  />
                  {config.crm_api_key && (
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => copyToClipboard(config.crm_api_key)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {newApiKey && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <Label className="text-emerald-800">New API Key (copy now!)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input 
                    value={newApiKey} 
                    readOnly 
                    className="font-mono bg-white"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(newApiKey)}
                    className="border-emerald-500 text-emerald-700"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-emerald-700 mt-2">
                  Save this key securely. It will not be shown again!
                </p>
              </div>
            )}

            <Button 
              onClick={generateNewKey} 
              disabled={generatingKey}
              className="gap-2"
            >
              {generatingKey ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Generate New API Key
            </Button>
          </CardContent>
        </Card>

        {/* Webhook Configuration */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Webhook className="h-5 w-5" />
              Webhook Configuration
            </CardTitle>
            <CardDescription>
              Configure webhook URL to receive notifications after bulk submissions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Webhook</Label>
                <p className="text-sm text-muted-foreground">
                  Send notifications to CRM after processing
                </p>
              </div>
              <Switch 
                checked={config.webhook_enabled}
                onCheckedChange={toggleWebhook}
              />
            </div>

            <div className="space-y-2">
              <Label>Webhook URL</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://your-crm.com/api/webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={saveWebhookConfig} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-2">Webhook Payload Format</h4>
              <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">
{`{
  "request_id": "CRM-REQ-20260207-001",
  "status": "COMPLETED",
  "stored": 90,
  "duplicates": 8,
  "invalid": 2,
  "timestamp": "2026-02-07T10:30:00Z"
}`}
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* API Documentation */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              API Documentation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium">Authentication</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  All API requests must include the API key in the header:
                </p>
                <code className="text-sm bg-background px-2 py-1 rounded">
                  X-API-KEY: your_api_key_here
                </code>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium">1. Bulk Submit</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  POST /api/external/source/bulk-submit
                </p>
                <p className="text-xs font-medium text-muted-foreground mt-3 mb-1">Request:</p>
                <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">
{`{
  "request_id": "CRM-REQ-001",
  "source_type": "NEWSPAPER",
  "source_name": "GUJARAT SAMACHAR",
  "source_date": "2025-05-25",
  "submitted_by": "admin@crm.com",
  "data": [
    { "seq": 1, "value": "7016254929" },
    { "seq": 2, "value": "9376138490" }
  ]
}`}
                </pre>
                <p className="text-xs font-medium text-muted-foreground mt-3 mb-1">Response:</p>
                <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">
{`{
  "request_id": "CRM-REQ-001",
  "total_received": 2,
  "stored": 2,
  "duplicates": 0,
  "invalid": 0,
  "results": [
    {
      "seq": 1,
      "input": "7016254929",
      "source_uid": "NP202505252160CF",
      "status": "UNIQUE"
    },
    {
      "seq": 2,
      "input": "9376138490",
      "source_uid": "NP202505253E0C2C",
      "status": "UNIQUE"
    }
  ]
}`}
                </pre>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium">2. Search</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  GET /api/external/source/search
                </p>
                <p className="text-xs font-medium text-muted-foreground mt-2 mb-1">Query Parameters:</p>
                <code className="text-xs bg-background px-2 py-1 rounded block mt-1">
                  ?source_uid=NP202505252160CF
                </code>
                <code className="text-xs bg-background px-2 py-1 rounded block mt-1">
                  ?value=7016254929
                </code>
                <p className="text-xs font-medium text-muted-foreground mt-3 mb-1">Response:</p>
                <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">
{`{
  "source_uid": "NP202505252160CF",
  "source_type": "NEWSPAPER",
  "source_name": "GUJARAT SAMACHAR",
  "source_date": "2025-05-25",
  "raw_value": "7016254929",
  "status": "ACTIVE",
  "created_at": "2026-02-07T10:00:00Z"
}`}
                </pre>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium">3. Bulk Search</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  GET /api/external/source/bulk-search
                </p>
                <p className="text-xs font-medium text-muted-foreground mt-2 mb-1">Query Parameters:</p>
                <code className="text-xs bg-background px-2 py-1 rounded block mt-1">
                  ?values=7016254929,9376138490,8765432109
                </code>
                <p className="text-xs font-medium text-muted-foreground mt-3 mb-1">Response:</p>
                <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">
{`{
  "total_searched": 3,
  "found": 2,
  "not_found": 1,
  "results": [
    {
      "value": "7016254929",
      "found": true,
      "source_uid": "NP202505252160CF",
      "source_type": "NEWSPAPER",
      "source_name": "GUJARAT SAMACHAR",
      "status": "ACTIVE"
    },
    {
      "value": "9376138490",
      "found": true,
      "source_uid": "NP202505253E0C2C",
      "source_type": "NEWSPAPER",
      "source_name": "GUJARAT SAMACHAR",
      "status": "ACTIVE"
    },
    {
      "value": "8765432109",
      "found": false,
      "source_uid": null,
      "source_type": null,
      "source_name": null,
      "status": null
    }
  ]
}`}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Request History */}
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Recent API Requests
              </CardTitle>
              <CardDescription>
                History of external CRM submissions
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchRequests} disabled={requestsLoading}>
              {requestsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No external requests yet
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Request ID</TableHead>
                      <TableHead>Source Type</TableHead>
                      <TableHead>Source Name</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead>Stored</TableHead>
                      <TableHead>Duplicates</TableHead>
                      <TableHead>Invalid</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-xs">{req.request_id}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{req.source_type}</Badge>
                        </TableCell>
                        <TableCell>{req.source_name || '-'}</TableCell>
                        <TableCell>{req.total_received}</TableCell>
                        <TableCell className="text-emerald-600">{req.stored}</TableCell>
                        <TableCell className="text-amber-600">{req.duplicates}</TableCell>
                        <TableCell className="text-red-600">{req.invalid}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(req.created_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CRMIntegrationPage;
